import React, { } from "react";
import { Link } from "react-router-dom";
import ContentWithLoader from "sr/layout/common/ContentLoader";
import { Pagination } from "app/pages/component/Pagination";
import { Empty } from "../candidates/component/Empty";

import { MetaBox } from 'sr/helpers/ui-components/MetaBox'
import { StatusPill } from 'sr/helpers/ui-components/StatusPill'
import { useIntl } from 'react-intl'
import { useEffect, useState } from 'react'
import { getPricingType } from 'sr/utils/api/masterData/getPricingType'

type Bidding = {
  id: string;
  corporate_id: { _id: string; corporate_name: string };
  requirement_id: { _id: string; title: string; end_date?: string };
  description: string;
  pricing_type: "Fixed" | "Hourly";
  bid_amount: string;
  status: string;
  createdAt?: string;
};


const BiddingsList: React.FC<{
  projects: Bidding[];
  loader: boolean;
  page: number;
  totalPage: number;
  setPage: (p: number) => void;
}> = ({ projects, loader, page, totalPage, setPage }) => {
  const intl = useIntl();
  const [pricingLabelMap, setPricingLabelMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getPricingType();
        if (res?.success && Array.isArray(res.data)) {
          const map: Record<string, string> = {};
          res.data.forEach((i: any) => {
            map[i.value] = i.label;
          });
          setPricingLabelMap(map);
        }
      } catch {}
    };
    load();
  }, []);

  return (
    <ContentWithLoader
      loading={loader}
      content={
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="p-6 flex flex-col rounded-[20px] mb-6 border animate-pulse">
              <div className="flex justify-between items-center mb-3">
                <div className="h-5 w-40 bg-gray-200 rounded" />
                <div className="h-4 w-16 bg-gray-200 rounded-full" />
              </div>
              <div className="h-3 w-3/4 bg-gray-200 rounded mb-3" />
              <div className="flex gap-3">
                <div className="h-10 w-40 bg-gray-200 rounded" />
                <div className="h-10 w-28 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      }
    >
      {projects.length > 0 ? (
        <>
          {projects.map((bid) => (
            <Link
              to={`/projects/bid-details/${bid.id}`}
              key={bid.id}
              className="group p-6 flex flex-col rounded-[20px] mb-6 border hover:shadow hover:bg-gray-50 transition"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-[#0078ae] group-hover:underline">
                  {bid.requirement_id?.title || "Untitled Requirement"}
                </h3>
                <StatusPill status={bid.status} />
              </div>

              <p className="text-sm text-gray-500 mb-2">
                Corporate:{" "}
                <span className="font-medium text-gray-800">
                  {bid.corporate_id?.corporate_name || "—"}
                </span>
              </p>

              <div
                className="text-sm text-gray-600 mb-4"
                dangerouslySetInnerHTML={{ __html: bid.description }}
              />

              <div className="flex flex-wrap gap-3 mb-5">
                <MetaBox label="Pricing" value={
                  bid?.pricing_type
                    ? intl.formatMessage({ id: pricingLabelMap[String(bid.pricing_type)] || String(bid.pricing_type), defaultMessage: String(bid.pricing_type) })
                    : '—'
                } />
                <MetaBox label="Bid Amount" value={bid.bid_amount || "—"} />
                <MetaBox
                  label="Created"
                  value={
                    bid.createdAt
                      ? new Date(bid.createdAt).toLocaleDateString()
                      : "—"
                  }
                />
              </div>
            </Link>
          ))}
          <Pagination page={page} totalPage={totalPage} setPage={setPage} />
        </>
      ) : (
        !loader && <Empty text="No biddings found" />
      )}
    </ContentWithLoader>
  );
};

export default BiddingsList;
